/* eslint-disable jsx-a11y/alt-text */
import { Box, Button, Container, Stack } from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Footer } from '../../components/Footer'
import { Form, FormProps } from '../../components/Form'
import {
  Goods,
  GoodsItem,
  mapPacks,
  mapPlans,
  mapProducts,
} from '../../components/Goods'
import { Header } from '../../components/Header'
import {
  Promo,
  PromoCodeField,
  PromoCodeFieldProps,
} from '../../components/PromoCodeField'
import { useGet } from '../../hooks/get'
import { usePurchase } from '../../hooks/purchase'
import { useSign } from '../../hooks/sign'
import { useUser } from '../../hooks/user'
import { PlanService } from '../../services/plan'
import s from './Pricing.module.css'

const Pricing: FC = () => {
  const { t } = useTranslation()
  const confirm = useConfirm()

  const { data: plans } = useGet<Dto.Plan[]>(`member_portal/plans`)
  const { data: packs } = useGet<Dto.Pack[]>(`member_portal/packs`)
  const { data: products } = useGet<Dto.Product[]>(`member_portal/products`)

  const { user } = useUser()

  const [selectedItem, setSelectedItem] = useState<GoodsItem>()
  const promoRef = useRef<Promo>()

  const sign = useSign()
  const purchase = usePurchase(selectedItem?.dto)

  const handlePromoCodeApply = useCallback<PromoCodeFieldProps['onApply']>(
    (promo) => {
      promoRef.current = promo
    },
    []
  )

  const handleSubmit: FormProps['onSubmit'] = async (fields) => {
    if (!selectedItem) {
      toast.error('Please select an item first.')
      return
    }

    if (!user) {
      await sign.start({
        purchase: {
          item: selectedItem.dto,
          promo: promoRef.current,
        },
      })
      return
    }

    try {
      await purchase.submit({ promo: promoRef.current })
      confirm({
        title: null,
        content: t('Purchase completed successfully.'),
        hideCancelButton: true,
      })
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      }
    }
  }

  const groups = useMemo(() => {
    if (!(plans && packs)) {
      return
    }

    const Trials = mapPlans(plans.filter(PlanService.isTrial.bind(PlanService)))
    const Packs = [
      ...mapPacks(packs || []),
      ...mapPlans(
        plans.filter(
          (plan) =>
            plan.version &&
            plan.details &&
            [4, 7].includes(plan.details.payment.payment_schedule)
        )
      ),
    ]

    const isSortedTo = (items: GoodsItem[], plan: Dto.Plan) =>
      items.find((item) => item.dto === plan)

    const Memberships = mapPlans(
      plans.filter(
        (plan) => !isSortedTo(Trials, plan) && !isSortedTo(Packs, plan)
      )
    )

    return {
      Trials,
      Memberships,
      Packs,
      Products: mapProducts(products || []),
    }
  }, [packs, plans, products])

  return (
    <>
      <Helmet>
        <title>{t('Pricing')}</title>
      </Helmet>
      <Header />
      <Container classes={{ root: s.root }}>
        <Box py={5}>
          <Goods items={groups || []} large onSelect={setSelectedItem} />
          <div className={s['form-wrap']}>
            {selectedItem && (
              <Box mb={2}>
                <PromoCodeField
                  goods={selectedItem.dto}
                  onApply={handlePromoCodeApply}
                />
              </Box>
            )}
            <Form onSubmit={handleSubmit}>
              <Stack alignItems="center" spacing={2}>
                {purchase.tcElement}
                <Button
                  type="submit"
                  loading={purchase.pending}
                  sx={{ minInlineSize: '8em' }}
                >
                  {t('Continue')}
                </Button>
              </Stack>
            </Form>
          </div>
          <Footer />
        </Box>
      </Container>
      {sign.element}
      {purchase.paymentMethodElement}
    </>
  )
}

export { Pricing }
